import {
    RiAddLine,
    RiDeleteBinLine, RiDraggable,
} from "@remixicon/react";
import classNames from "classnames";
import { } from "native-injects";
import React from "react";
import {
    MetricsDashboardGraphDimension,
    MetricsDashboardGraphDimensionFormat,
    MetricsDashboardGraphMetric,
    MetricsDashboardGraphTypeDisplayType,
} from "../../../__generated__/api";
import ReportFieldSelector, { useFlatFields } from "../../../components/pickers/field-selector/ReportFieldSelector";
import Button from "../../../components/theme/button/Button";
import FormGroup from "../../../components/theme/form-group/FormGroup";
import SortableList from "../../../components/theme/sortable/SortableList";
import Styles from "../Reports.module.scss";
import {
    GraphSettingsProperties,
    isDate,
    isDimension,
    isMetric,
    isNumeric,
} from "./base";
import DisplayOptions from "./DisplayOptions";
import FormatOptions from "./FormatOptions";
import { MetricAggregation } from "./MetricProperties";

export interface GraphColumnSettingsProperties extends GraphSettingsProperties {
    label: string;
    helpText: string;
    field: "dimensions" | "metrics";
    canSetAggregation?: boolean;
    canAddFormat?: boolean;
    canAddLabel?: boolean;
}

function excludeId<T>(props: T & {id?: string}): T {
    if (props && "id" in props) {
        // eslint-disable-next-line no-param-reassign
        // delete props.id;
    }
    return props;
}

export default function ColumnProperties(props: GraphColumnSettingsProperties) {
    const { flatFields } = useFlatFields(props.graph.dataSource);
    const dimensions = props.graph[props.field]?.map((row, index) => ({
        row,
        id: row.fieldId as string ?? `index:${index}`,
    })) ?? [];

    return (
        <FormGroup
            label={props.label}
            required
            size="small"
            helpDisplay="icon"
            helpText={props.helpText}
        >
            <SortableList
                id="dimensions"
                items={dimensions}
                className={Styles["table-edit-columns"]}
                onReorder={ordered => {
                    props.onChange(excludeId({
                        ...props.graph,
                        [props.field]: ordered.toIdList("row"),
                    }));
                }}
            >
                {dimensions.map((dimension, index) => {
                    const selector = (
                        <ReportFieldSelector
                            dataSource={props.graph.dataSource}
                            value={dimension.id}
                            onChange={value => {
                                const copy = dimensions.toIdList("row");
                                copy[index] = {
                                    ...dimension.row,
                                    fieldId: value?.field.id,
                                };
                                const metric = copy[index] as MetricsDashboardGraphMetric;
                                if (props.field === "metrics" && isNumeric(value?.field.type)) {
                                    metric.display = MetricsDashboardGraphTypeDisplayType.Float;
                                } else {
                                    delete metric.display;
                                }
                                const dimensionField = copy[index] as MetricsDashboardGraphDimension;
                                if (props.field === "dimensions" && isDate(value?.field.type)) {
                                    dimensionField.format = MetricsDashboardGraphDimensionFormat.YearMonthDayHourMinute;
                                } else {
                                    delete dimensionField.format;
                                }
                                props.onChange(excludeId({
                                    ...props.graph,
                                    [props.field]: copy,
                                }));
                            }}
                        />
                    );

                    return (
                        <SortableList.Item
                            id={dimension.id}
                            shouldDragWholeElement={false}
                            item={dimension}
                            key={dimension.id}
                            className={Styles["table-edit-column"]}
                        >
                            <SortableList.Listener>
                                <RiDraggable className={Styles["table-move-icon"]} />
                            </SortableList.Listener>
                            <div className={Styles["table-edit-column-values"]}>
                                {(props.canSetAggregation || props.canAddFormat) ? (
                                    <label className="form-label">
                                        Field
                                        {selector}
                                    </label>
                                ) : selector}

                                {(props.canSetAggregation || props.canAddLabel) && (
                                    <div className={
                                        classNames(Styles[
                                            `table-edit-column-properties${props.canSetAggregation ? "-aggregate" : ""}`
                                        ])
                                    }
                                    >
                                        {props.canSetAggregation && (
                                            <MetricAggregation index={index} {...props} />
                                        )}

                                        {props.canAddLabel && (
                                            <FormGroup
                                                label="Label"
                                                type="text"
                                                value={dimension.row.label ?? ""}
                                                placeholder={flatFields[dimension.id]?.column.field?.name}
                                                size="small"
                                                onChange={evt => {
                                                    const copy = dimensions.toIdList("row");
                                                    copy[index] = {
                                                        ...dimension.row,
                                                        label: evt.target.value,
                                                    };
                                                    props.onChange(excludeId({
                                                        ...props.graph,
                                                        [props.field]: copy,
                                                    }));
                                                }}
                                            />
                                        )}

                                    </div>
                                )}
                                <div className={
                                    classNames(Styles[`table-edit-column-properties${props.canAddFormat ? "-aggregate" : ""}`])
                                }
                                >
                                    {props.canAddFormat && isDimension(dimension.row) && (
                                        <FormGroup
                                            label="Format"
                                            size="small"
                                        >
                                            <FormatOptions
                                                value={(
                                                    dimension.row as MetricsDashboardGraphDimension
                                                ).format as MetricsDashboardGraphDimensionFormat}
                                                onChange={format => {
                                                    const copy = dimensions.toIdList("row");
                                                    copy[index] = {
                                                        ...dimension.row,
                                                        format: format as MetricsDashboardGraphDimensionFormat,
                                                    } as MetricsDashboardGraphDimension;
                                                    props.onChange(excludeId({
                                                        ...props.graph,
                                                        [props.field]: copy,
                                                    }));
                                                }}
                                            />
                                        </FormGroup>
                                    )}
                                    {props.canAddFormat && isMetric(dimension.row) && (
                                        <FormGroup
                                            label="Format"
                                            size="small"
                                        >
                                            <DisplayOptions
                                                value={(
                                                    dimension.row as MetricsDashboardGraphMetric
                                                ).display as MetricsDashboardGraphTypeDisplayType}
                                                onChange={display => {
                                                    const copy = dimensions.toIdList("row");
                                                    copy[index] = {
                                                        ...dimension.row,
                                                        display: display as MetricsDashboardGraphTypeDisplayType,
                                                    } as MetricsDashboardGraphMetric;
                                                    props.onChange(excludeId({
                                                        ...props.graph,
                                                        [props.field]: copy,
                                                    }));
                                                }}
                                            />
                                        </FormGroup>
                                    )}

                                </div>
                            </div>

                            <Button
                                aria-label="Delete Column"
                                type="button"
                                variant="none"
                                className={Styles["table-edit-delete"]}
                                prefixIcon={<RiDeleteBinLine />}
                                onClick={() => {
                                    const copy = Array.from(dimensions);
                                    copy.splice(index, 1);
                                    props.onChange(excludeId({
                                        ...props.graph,
                                        [props.field]: copy.toIdList("row"),
                                    }));
                                }}
                            />

                        </SortableList.Item>
                    );
                })}
            </SortableList>
            <Button
                prefixIcon={<RiAddLine />}
                size="small"
                variant="outline-blue"
                onClick={() => {
                    const copy = Array.from(dimensions);
                    copy.push({
                        row: Object.create(null),
                        id: "",
                    });
                    props.onChange(excludeId({
                        ...props.graph,
                        [props.field]: copy.toIdList("row"),
                    }));
                }}
            >
                Add
            </Button>

        </FormGroup>
    );
}

ColumnProperties.defaultProps = {
    canSetAggregation: false,
    canAddFormat: false,
    canAddLabel: false,
};
